import type { Item } from "./types";

const uid = () => Math.random().toString(36).slice(2, 10);

export interface MergeCandidate {
  desk: Item;
  members: Item[];
  chairCount: number;
  expectedSeats: number;
  reason?: string;
}

/**
 * If the selection (or its enclosing group) contains exactly one exploded
 * workstation desk plus its chairs, return a candidate that can be merged
 * back into the original parametric workstation. Returns null otherwise.
 */
export function findMergeCandidate(selected: Item[], allItems: Item[]): MergeCandidate | null {
  const groupIds = Array.from(new Set(selected.map((i) => i.groupId).filter(Boolean) as string[]));
  if (groupIds.length !== 1) return null;
  const gid = groupIds[0];
  const members = allItems.filter((i) => i.groupId === gid);
  const desks = members.filter((i) => i.shape === "desk-rect" && i.origin);
  if (desks.length !== 1) return null;
  const desk = desks[0];
  const chairs = members.filter((i) => i.shape === "chair");
  const expectedSeats = desk.origin?.seats ?? chairs.length;
  return {
    desk,
    members,
    chairCount: chairs.length,
    expectedSeats,
    reason: chairs.length !== expectedSeats
      ? `Need ${expectedSeats} chairs in the group, found ${chairs.length}.`
      : undefined,
  };
}

export function canMerge(c: MergeCandidate | null): boolean {
  return !!c && c.chairCount === c.expectedSeats;
}

/** Build the parametric workstation from the candidate. Preserves the desk's
 *  current position/rotation/size so the merged item lands where the desk is. */
export function mergeIntoWorkstation(candidate: MergeCandidate): Item {
  const { desk } = candidate;
  const o = desk.origin!;
  return {
    id: uid(),
    type: o.shape,
    kind: "furniture",
    shape: o.shape,
    label: o.label,
    x: desk.x,
    y: desk.y,
    w: desk.w,
    h: desk.h,
    rotation: desk.rotation,
    color: o.color,
    seats: o.seats,
  };
}